import { FC } from 'react';

import { TooltipProps } from 'recharts';

import { AnalyticsTooltipSeparator } from '../../AnalyticsTooltipSeparator';
import { IAnalyticsStackedTimeSeriesSegment } from '../AnalyticsStackedTimeSeriesBox.types';
import {
  findSegmentByKey,
  formatPayloadValue,
  getTooltipTitle,
  resolveTotalValue
} from '../AnalyticsStackedTimeSeriesBox.utils';

import { AnalyticsStackedTimeSeriesSegmentSwatch } from './AnalyticsStackedTimeSeriesSegmentSwatch';

interface IAnalyticsStackedTimeSeriesTooltipProps
  extends TooltipProps<number, string> {
  segments: IAnalyticsStackedTimeSeriesSegment[];
  valueFormatter: (value: number) => string;
  tooltipTitleFormatter?: (date: string) => string;
}

export const AnalyticsStackedTimeSeriesTooltip: FC<IAnalyticsStackedTimeSeriesTooltipProps> = ({
  active,
  payload,
  label,
  segments,
  valueFormatter,
  tooltipTitleFormatter
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const title = getTooltipTitle(label, tooltipTitleFormatter);
  const totalValue = resolveTotalValue(payload);

  return (
    <div className="rounded-md bg-base-card p-2 shadow-md border border-base-border text-xs flex flex-col gap-1 min-w-[150px]">
      <p className="font-bold text-base-foreground">{title}</p>

      {totalValue !== undefined && (
        <>
          <p className="font-medium text-base-foreground">
            Total: {valueFormatter(totalValue)}
          </p>
          <AnalyticsTooltipSeparator />
        </>
      )}

      <div className="flex flex-col gap-1">
        {payload.map((entry) => {
          const matchingSegment = findSegmentByKey(segments, entry.dataKey);
          const swatchColor = matchingSegment?.color ?? entry.color;
          const segmentLabel = matchingSegment?.label ?? entry.name;
          const formattedValue = formatPayloadValue(entry.value, valueFormatter);

          return (
            <div key={entry.dataKey} className="flex items-center gap-2">
              <AnalyticsStackedTimeSeriesSegmentSwatch color={swatchColor} />
              <span className="text-base-sidebarRing">
                {segmentLabel} : {formattedValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
